/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

/**
 * Converts Celsius to Fahrenheit
 * @param celsius Temperature in Celsius
 * @returns Temperature in Fahrenheit
 */
export const celsiusToFahrenheit = (celsius: number): number => {
  return (celsius * 9) / 5 + 32
}

/**
 * Converts Fahrenheit to Celsius
 * @param fahrenheit Temperature in Fahrenheit
 * @returns Temperature in Celsius
 */
export const fahrenheitToCelsius = (fahrenheit: number): number => {
  return ((fahrenheit - 32) * 5) / 9
}

/**
 * Formats the temperature according to the specified unit
 * @param celsius Temperature in Celsius (can be null or undefined)
 * @param unit Desired temperature unit ('C' or 'F')
 * @returns Formatted temperature string with unit or 'N/A' if temperature is null/undefined
 */
export const formatTemperature = (celsius: number | null | undefined, unit: 'C' | 'F'): string => {
  // Return 'N/A' if temperature is null, undefined, NaN, or not a finite number
  if (celsius === null || celsius === undefined || typeof celsius !== 'number' || !Number.isFinite(celsius)) {
    return 'N/A'
  }

  if (unit === 'C') {
    return `${Number(celsius).toFixed(1)}°C`
  } else {
    const fahrenheit = celsiusToFahrenheit(celsius)
    return `${Number(fahrenheit).toFixed(1)}°F`
  }
}

export const celsiusToUser = (
  c: number | null | undefined,
  digits = 1,
  units: 'Imperial' | 'Metric' = 'Imperial'
): string => {
  // Samples can be missing a reading (null/undefined) — render an empty cell rather
  // than crashing on toFixed. Mirrors the `? … : ''` guards at the call sites.
  if (c === null || c === undefined || typeof c !== 'number' || !Number.isFinite(c)) {
    return ''
  }
  return units === 'Metric' ? c.toFixed(digits) : ((c * 9) / 5 + 32).toFixed(digits)
}

/** Convert a user's preferred units to the Celsius value, returning a number. */
export const userToCelsius = (value: number, digits = 1, units: 'Imperial' | 'Metric' = 'Imperial'): number =>
  Number((units === 'Metric' ? value : ((value - 32) * 5) / 9).toFixed(digits))

/** Convert a Celsius value to the user's preferred units, returning a number. */
export const convertCelsius = (c: number, units: 'Imperial' | 'Metric'): number =>
  units === 'Metric' ? c : (c * 9) / 5 + 32

/** Degree symbol with unit suffix for the user's preferred units. */
export const tempUnitSuffix = (units: 'Imperial' | 'Metric'): '°C' | '°F' => (units === 'Metric' ? '°C' : '°F')
